import {
  BRAND_HAS_WHATSAPP_SHARE,
  TWITTER_HANDLE
} from 'common/configuration/constants';
import { AD_ATF } from 'common/constants/Ads';
import eventEmitter, { EventsTypes } from 'common/services/events/eventEmitter';
import * as events from 'common/tools/dom/events';
import readAttribute, {
  readNumberAttribute
} from 'common/tools/dom/readAttribute';
import swipeOn from 'common/tools/dom/swipe';
import { path } from 'common/tools/network/routing';
import { getImageUrl } from 'common/tools/network/url';
import { desobfuscateUrl } from 'common/tools/seo/desobfuscation';

import {
  GalleryComponents,
  GalleryItem,
  GallerySize
} from 'website/module/ui/async-gallery/types';

const SIZES: Record<keyof GallerySize, { format: { rw: number; rh: number } }> =
  {
    SIZE_DEFAULT: { format: { rw: 1280, rh: 720 } },
    SIZE_FB: { format: { rw: 640, rh: 360 } },
    SIZE_4COL: { format: { rw: 1920, rh: 1080 } },
    SIZE_3COL: { format: { rw: 1280, rh: 720 } },
    SIZE_2COL: { format: { rw: 1024, rh: 576 } },
    SIZE_1COL: { format: { rw: 512, rh: 288 } }
  };

export const fragEl = document.createDocumentFragment();
const imgEl = document.createElement('img');
const italicsEl = document.createElement('i');
export const linkEl = document.createElement('a');
const sourceEl = document.createElement('source');
export const spanEl = document.createElement('span');

export const pipe = (() => {
  const pipeEl = spanEl.cloneNode() as HTMLSpanElement;
  const pipeText = document.createTextNode('|');
  pipeEl.appendChild(pipeText);
  pipeEl.classList.add('pipe');
  return pipeEl;
})();

export const comaSeparator = document.createTextNode(', ');

export const cleanDOM = (element: Element) => {
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
  return element;
};

const buildButton = (
  className: string,
  href: string,
  icon: string,
  text: string
) => {
  const buttonEl = linkEl.cloneNode() as HTMLAnchorElement;
  buttonEl.classList.add(
    'button',
    `button-${className}-full-square`,
    'js-share-btn'
  );
  buttonEl.href = href;

  const iconEl = italicsEl.cloneNode() as HTMLElement;
  iconEl.classList.add('icon', `icon-social-${icon}-square`);

  const textContainer = spanEl.cloneNode() as HTMLSpanElement;
  textContainer.classList.add('txt');

  const textEl = document.createTextNode(text);

  textContainer.appendChild(textEl);

  buttonEl.appendChild(iconEl);
  buttonEl.appendChild(textContainer);
  return buttonEl;
};

interface GalleryInterface {
  changeIndex?: (
    direction: number
  ) => Promise<boolean | undefined> | boolean | undefined;
  gaPush?: () => void;
  render?: () => void;
  rewriteHistory?: (href?: string) => void;
}

export default class AbstractGallery implements GalleryInterface {
  protected components?: GalleryComponents;
  protected currentIndex?: number;
  private readonly entityId?: number;
  protected items?: GalleryItem[];
  protected root: HTMLElement;
  protected readonly startPath: string | null;
  private readonly size: GallerySize;

  changeIndex?: (
    direction: number
  ) => boolean | Promise<boolean | undefined> | undefined;
  gaPush?: () => void;
  render?: () => void;
  rewriteHistory?: (href?: string | URL | null) => void;

  constructor(element: HTMLElement) {
    this.root = element;

    const univers = readAttribute<string, undefined>(element, 'data-universe');
    this.startPath = null;
    this.entityId = readNumberAttribute(element, 'data-entity-id');

    this.size = {};

    switch (univers) {
      case 'moviepage':
        this.startPath = path('moviepage_shot', { movie: this.entityId });
        break;
      case 'personpage':
        this.startPath = path('personpage_shot', { person: this.entityId });
        break;
      case 'seriespage':
        this.startPath = path('seriespage_shot', { series: this.entityId });
        break;
      default:
        throw new Error('Unknown universe');
    }
  }

  finishInitialisation = () => {
    if (this.components) {
      this.components.previousLink = this.root.getElementsByClassName(
        'js-previous'
      )[0] as HTMLAnchorElement | undefined;
      this.components.nextLink = this.root.getElementsByClassName(
        'js-next'
      )[0] as HTMLAnchorElement | undefined;
      this.initEvents();
      swipeOn(this.components.container, this.handleSwipe);
    }
  };

  handleSwipe = (element: Element | undefined, direction: string | null) => {
    if (element === this.components?.container) {
      switch (direction) {
        case 'SWIPE_LEFT':
          this.changeIndex?.(-1);
          break;
        case 'SWIPE_RIGHT':
          this.changeIndex?.(+1);
          break;
        default:
          return false;
      }
    }
  };

  initEvents = () => {
    if (this.components?.previousLink)
      events.on(this.components.previousLink, 'click', this.previous);
    if (this.components?.nextLink)
      events.on(this.components.nextLink, 'click', this.next);
  };

  previous = (event: Event) => {
    event.preventDefault();
    this.changeIndex?.(-1);
    return false;
  };

  next = (event: Event) => {
    event.preventDefault();
    this.changeIndex?.(+1);
    return false;
  };

  emitUpdate = () => {
    const eventPayload = {
      source: 'async-gallery',
      photo: this.items?.[this.currentIndex ?? 0].id,
      movie: this.entityId,
      advertisementTargets: [AD_ATF]
    };
    eventEmitter.emit(EventsTypes.ASYNCHRONOUS_PAGE_CHANGE, eventPayload);
  };

  setSrcSizeUrl = (src?: string) => {
    let size: keyof GallerySize;
    for (size in SIZES) {
      this.size[size] = getImageUrl({ src, ...SIZES[size] });
    }
  };

  changePicture = (description?: string) => {
    const source4col = sourceEl.cloneNode() as HTMLSourceElement;
    source4col.media = '(min-width: 64em)';
    source4col.srcset = this.size.SIZE_4COL ?? '';

    const source3col = sourceEl.cloneNode() as HTMLSourceElement;
    source3col.media = '(min-width: 45em)';
    source3col.srcset = this.size.SIZE_3COL ?? '';

    const source2col = sourceEl.cloneNode() as HTMLSourceElement;
    source2col.media = '(min-width: 30em)';
    source2col.srcset = this.size.SIZE_2COL ?? '';

    const source1col = sourceEl.cloneNode() as HTMLSourceElement;
    source1col.media = '(min-width: 1em)';
    source1col.srcset = this.size.SIZE_1COL ?? '';

    const img = imgEl.cloneNode() as HTMLImageElement;
    img.classList.add('gallery-media', 'photo');
    img.src = this.size.SIZE_DEFAULT ?? '';
    img.alt = description ?? '';

    if (this.components?.container) {
      cleanDOM(this.components.container).appendChild(source4col);
      this.components.container.appendChild(source3col);
      this.components.container.appendChild(source2col);
      this.components.container.appendChild(source1col);
      this.components.container.appendChild(img);
    }
  };

  changeSocialButtons = () => {
    const currentItem = this.items?.[this.currentIndex ?? 0];
    const url = encodeURIComponent(window.location.href);
    const socialButtons = fragEl.cloneNode();

    if (BRAND_HAS_WHATSAPP_SHARE) {
      const whatsappLink = buildButton(
        'whatsapp',
        `https://api.whatsapp.com/send?text=${url}`,
        'whatsapp',
        'whatsapp'
      );
      socialButtons.appendChild(whatsappLink);
    }
    const facebookLink = buildButton(
      'fb',
      `https://www.facebook.com/sharer/sharer.php?u=${desobfuscateUrl(url)}`,
      'facebook',
      'facebook'
    );
    socialButtons.appendChild(facebookLink);

    const twitterLink = buildButton(
      'tw',
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        currentItem?.description ?? ''
      )}&url=${desobfuscateUrl(url)}&via=${TWITTER_HANDLE}`,
      'twitter',
      'Tweet'
    );
    socialButtons.appendChild(twitterLink);

    if (this.components?.social)
      cleanDOM(this.components.social).appendChild(socialButtons);
  };
}
